import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { NtIcon } from './NotionUI';
import { userColor } from '../utils/userColor';

const initialsOf = (u) => `${(u.name || '')[0] || ''}${(u.surname || '')[0] || ''}`.toUpperCase() || '—';
const fullNameOf = (u) => [u.name, u.surname].filter(Boolean).join(' ');

/**
 * Collaboratori di un ticket (o di una coda): operatori coinvolti oltre al responsabile.
 * Mostra chi collabora come etichette con la x per toglierlo, e un menu per aggiungerne altri.
 * `onChange` riceve l'elenco completo dei nuovi id.
 */
function CollaboratorsPicker({ collaborators = [], agents, primaryId, onChange, me = null }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const ids = collaborators.map((c) => c.id);
  const nameOf = (u) => (u.id === me ? `${fullNameOf(u)} (${t('tickets.you')})` : fullNameOf(u));
  const available = agents.filter((a) => a.id !== primaryId && !ids.includes(a.id));

  return (
    <div className="collab" ref={rootRef} title={t('tickets.collaboratorsHint')}>
      {collaborators.map((c) => (
        <span key={c.id} className="collab-chip">
          <span className="collab-av" style={{ background: userColor(c) }} aria-hidden="true">{initialsOf(c)}</span>
          <span className="collab-name">{nameOf(c)}</span>
          <button
            type="button"
            className="collab-remove"
            title={t('tickets.removeCollaborator')}
            aria-label={`${t('tickets.removeCollaborator')}: ${fullNameOf(c)}`}
            onClick={() => onChange(ids.filter((id) => id !== c.id))}
          >
            ×
          </button>
        </span>
      ))}
      {collaborators.length === 0 && <span className="collab-none">{t('tickets.noCollaborators')}</span>}
      <span className="collab-add-wrap">
        <button
          type="button"
          className="collab-add"
          aria-haspopup="menu"
          aria-expanded={open}
          disabled={available.length === 0}
          onClick={() => setOpen((o) => !o)}
        >
          <NtIcon name="plus" size={13} /> {t('tickets.addCollaborator')}
        </button>
        {open && (
          <div className="collab-menu" role="menu">
            {available.map((a) => (
              <button
                key={a.id}
                type="button"
                role="menuitem"
                className="collab-option"
                onClick={() => {
                  setOpen(false);
                  onChange([...ids, a.id]);
                }}
              >
                <span className="collab-av" style={{ background: userColor(a) }} aria-hidden="true">{initialsOf(a)}</span>
                <span>{nameOf(a)}</span>
              </button>
            ))}
          </div>
        )}
      </span>
    </div>
  );
}

export default CollaboratorsPicker;
